import type { ProFormColumnsType } from '@ant-design/pro-components';
import type { BtlApplication } from '@api/btlApplicationForm/types';
import { ApplicationStatus } from '@api/btlApplicationForm/types';
import { App } from 'antd';
import { standardFormValidationProps } from '@common/formValidation';
import { firmApi } from '@api/firm/firm.api';
import { userApi } from '@api/user/userApi';
import {
  getAdjacentOoCaseSidebarItemId,
  getAdjacentOoCaseSidebarUrlSection,
  getOoCaseSidebarNavigationLabel,
} from '@components/CaseSidebar/navigation/ooCaseSidebarNavigation';
import type { OoCaseSidebarContext } from '@components/CaseSidebar/buildOoCaseSidebar';
import { isOoCaseEditableStatus, isAfterOoDipAccepted } from '@components/CaseSidebar/config/ooStatus';
import { CaseSidebarItemId } from '@components/CaseSidebar/types';
import { FormValidationContext } from '@hooks/useBtlFormValidation';
import { isDipAcceptedOrLater } from '@pages/BtlApplicationFormPage/constants';
import { ooFinancingFormSchema } from '@pages/BtlApplicationFormPage/FinancingAndProperty/formSchema';
import { hydrateOoFinancingFormValues } from '@pages/BtlApplicationFormPage/FinancingAndProperty/mapApplicationToFormValues';
import { submitFinancingAndPropertyForm } from '@pages/BtlApplicationFormPage/FinancingAndProperty/submitFinancingAndPropertyForm';
import { makeFormReadonly } from '@pages/BtlApplicationFormPage/makeFormReadonly';
import { useBrokerFirm } from '@queries/broker/useBrokerFirm';
import { useAuth } from '@providers/AuthProvider/AuthContext';
import { useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getPageURLWithSection, PageURL } from '@router/pageURL';
import { shouldShowOoTermField } from './shouldShowOoTermField';

interface UseOoCaseFinancingFormParams {
  caseId?: string;
  caseData?: BtlApplication;
  isCaseLoading?: boolean;
  sidebarContext?: OoCaseSidebarContext;
  canAccessHppPages?: boolean;
  isCreateMode?: boolean;
}

const EDITABLE_TERM_FIELD = 'termInMonths';

interface BuildOoFinancingFormColumnsOptions {
  canEditAll: boolean;
  canEditTermOnly: boolean;
  lockTermField?: boolean;
}

function buildOoFinancingFormColumns(
  schema: ProFormColumnsType[],
  options: BuildOoFinancingFormColumnsOptions,
): ProFormColumnsType[] {
  if (options.canEditAll) {
    if (options.lockTermField) {
      const readonlyTermColumns = makeFormReadonly(schema.filter(column => column.dataIndex === EDITABLE_TERM_FIELD));

      return [...schema.filter(column => column.dataIndex !== EDITABLE_TERM_FIELD), ...readonlyTermColumns];
    }

    return schema;
  }

  if (!options.canEditTermOnly) {
    return makeFormReadonly(schema);
  }

  const editableColumns = schema.filter(column => column.dataIndex === EDITABLE_TERM_FIELD);
  const readonlyColumns = makeFormReadonly(schema.filter(column => column.dataIndex !== EDITABLE_TERM_FIELD));

  return [...readonlyColumns, ...editableColumns];
}

export function isOoFinancingFormEditable(
  caseData?: BtlApplication,
  _canAccessHppPages?: boolean,
  isCreateMode?: boolean,
): boolean {
  if (isCreateMode) {
    return true;
  }

  return isOoCaseEditableStatus(caseData?.status);
}

export function useOoCaseFinancingForm({
  caseId = '',
  caseData,
  isCaseLoading = false,
  sidebarContext,
  canAccessHppPages = false,
  isCreateMode = false,
}: UseOoCaseFinancingFormParams) {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { formatMessage } = useIntl();
  const { isSignedIn } = useAuth();
  const [searchParams] = useSearchParams();
  const { data: firm, isLoading: isFirmLoading } = useBrokerFirm();
  const queryClient = useQueryClient();
  const formValidationContext = useContext(FormValidationContext);
  const formSectionParam = searchParams.get('section') ?? CaseSidebarItemId.FINANCING;
  const propertyId = caseData?.applicationProperty?.property?.id;
  const isDipAcceptedFullEdit = !isCreateMode && caseData?.status === ApplicationStatus.DIP_ACCEPTED;
  const isAfterFeesPaid = !isCreateMode && isAfterOoDipAccepted(caseData?.status);
  // HB-4457: term is HPP-only at every status, create included. Non-HPP brokers never see it.
  const showTermField = shouldShowOoTermField({ canAccessHppPages, isCreateMode });
  const isFormEditable = isOoFinancingFormEditable(caseData, canAccessHppPages, isCreateMode);
  const nextSection = isCreateMode
    ? CaseSidebarItemId.APPLICANTS
    : sidebarContext
    ? getAdjacentOoCaseSidebarUrlSection(formSectionParam, sidebarContext, 'next') ?? ''
    : '';

  if (isFormEditable && !formValidationContext) {
    throw new Error('OO financing form must be used within a FormValidationProvider when editable');
  }

  const setIsSubmitting = formValidationContext?.setIsSubmitting ?? (() => undefined);

  if (isCaseLoading || isFirmLoading) {
    return { isLoading: true as const };
  }

  // Do not fold isCreateMode into canAccessHppPages — that flag is firm/HPP entitlement only.
  // Create intentionally exposes intermediary fees via the explicit flag below. Term is not
  // one of them: it stays gated on HPP entitlement at every status (HB-4457).
  const schemaProps = {
    formatMessage,
    firm,
    isFormEditable,
    isDipAccepted: isDipAcceptedFullEdit,
    isDipAcceptedOrLater: isCreateMode ? false : isDipAcceptedOrLater(caseData?.status),
    intermediaryFees: caseData?.submissionRoute?.intermediaryFees ?? [],
    canAccessHppPages,
    showTermField,
    isIntermediaryFeesEditable: isCreateMode || isDipAcceptedFullEdit,
  };

  const formSchema = buildOoFinancingFormColumns(ooFinancingFormSchema(schemaProps), {
    canEditAll: isFormEditable,
    canEditTermOnly: false,
    lockTermField: !isCreateMode && isAfterFeesPaid,
  });

  return {
    isLoading: false as const,
    isFormEditable,
    footerProps: {
      previousSection:
        !isCreateMode && sidebarContext
          ? getAdjacentOoCaseSidebarItemId(formSectionParam, sidebarContext, 'previous')
          : undefined,
      nextSection: isCreateMode
        ? CaseSidebarItemId.APPLICANTS
        : sidebarContext
        ? getAdjacentOoCaseSidebarItemId(formSectionParam, sidebarContext, 'next')
        : undefined,
      previousLabel:
        !isCreateMode && sidebarContext
          ? getOoCaseSidebarNavigationLabel(formatMessage, formSectionParam, sidebarContext, 'previous')
          : undefined,
      nextLabel: isCreateMode
        ? formatMessage({ id: 'common.saveContinue' })
        : sidebarContext
        ? getOoCaseSidebarNavigationLabel(formatMessage, formSectionParam, sidebarContext, 'next')
        : undefined,
    },
    formProps: {
      columns: formSchema,
      isFormEditable,
      name: isCreateMode ? 'oo_financing_create' : 'oo_financing',
      ...standardFormValidationProps,
      request: async () => {
        let brokerReferrerId: string | undefined;

        if (isSignedIn) {
          try {
            const user = await userApi.getUser();
            if (user?.id) {
              const brokerReferrer = await firmApi.getBrokerReferrerByUserId(user.id);
              brokerReferrerId = brokerReferrer.id;
            }
          } catch {
            brokerReferrerId = undefined;
          }
        }

        return hydrateOoFinancingFormValues(caseData, firm, brokerReferrerId);
      },
      onFinish: isFormEditable
        ? async (values: Record<string, unknown>) => {
            await submitFinancingAndPropertyForm({
              values,
              data: caseData,
              firm,
              id: caseId,
              propertyId,
              nextSection,
              navigate: isCreateMode ? navigate : () => undefined,
              onNavigateAfterSubmit: isCreateMode ? undefined : section => navigate({ search: `?section=${section}` }),
              onNavigateAfterCreate: isCreateMode
                ? (appId, section) => {
                    navigate(getPageURLWithSection(PageURL.APPLICATION_DETAILS, { id: appId }, { section }));
                  }
                : undefined,
              applicationType: 'OWNER_OCCUPIED',
              queryClient,
              setIsSubmitting,
              message,
              showSuccessMessage: isCreateMode,
              // OO Financing is the only form that renders and edits deposit-source fields;
              // BTL Financing and OO Property must never echo hydrated server data back.
              includeDepositSources: true,
            });
          }
        : undefined,
    },
  };
}
